import { computed, ComputedRef } from "vue";
import { AbstractFileModel } from "./abstracts";
import {
  IFileApiService,
  IFileImportService,
  IFileUploadService,
  IImportFileApiService,
  IImportVideoApiService,
  IVideoApiService,
  IVideoImportService,
  IVideoUploadService,
  IFileManagerFacade,
} from "./interfaces";
import {
  VideoImportService,
  VideoUploadService,
  FileImportService,
  FileUploadService,
} from "./services";
import {
  Events,
  FileModel,
  ImportFileModel,
  ImportVideoModel,
  ResponseModel,
  VideoModel,
} from "./models";
import {
  ControlParams,
  EventsType,
  ImportBatchParams,
  ImportItem,
  ImportParams,
  UploadBatchParams,
  UploadFileItem,
  UploadParams,
} from "./types";
import { FileUploadStatusEnum } from "./enums";

export class FileManagerService implements IFileManagerFacade {
  /**Сервис проинициализирован */
  private isInited = false;
  /**Сервис загрузки файлов */
  private fileUploadService: IFileUploadService;
  /**Сервис загрузки видеофайлов */
  private videoUploadService: IVideoUploadService;
  /**Сервис импорта файлов */
  private fileImportService: IFileImportService;
  /**Сервис импорта видеофайлов */
  private videoImportService: IVideoImportService;
  /**Событие завершение загрузки/импорта файла */
  readonly CompleteEvent: EventsType<AbstractFileModel> =
    new Events<AbstractFileModel>();
  /**Событие начала обработки видеофайла */
  readonly StartProcessingEvent: EventsType<VideoModel | ImportVideoModel> =
    new Events<VideoModel | ImportVideoModel>();

  /**Включено ли предупреждающее окно при выходе со страницы */
  IsEnableExitAlert: boolean = true;

  /**Инициализировать менеджер файлов */
  readonly init = (
    fileUploadApi: IFileApiService,
    videoUploadApi: IVideoApiService,
    fileImportApi: IImportFileApiService,
    videoImportApi: IImportVideoApiService
  ) => {
    if (this.isInited) return;
    this.isInited = true;

    this.fileUploadService = new FileUploadService(fileUploadApi);
    this.videoUploadService = new VideoUploadService(videoUploadApi);
    this.fileImportService = new FileImportService(fileImportApi);
    this.videoImportService = new VideoImportService(videoImportApi);

    this.subscribeEvents();
    this.subscribeOnExit();
  };

  /**Подписаться на события */
  private subscribeEvents = () => {
    this.fileUploadService.CompleteEvent.on(this.onComplete);
    this.videoUploadService.CompleteEvent.on(this.onComplete);
    this.fileImportService.CompleteEvent.on(this.onComplete);
    this.videoImportService.CompleteEvent.on(this.onComplete);
    this.videoUploadService.StartProcessingEvent.on(this.onStartProcessing);
    this.videoImportService.StartProcessingEvent.on(this.onStartProcessing);
  };
  /**
   * Обработчик успешного завершения загрузки/импорта файла
   * @param event Данные события
   */
  private onComplete = (event: AbstractFileModel) => {
    this.CompleteEvent.trigger(event);
  };
  /**
   * Обработчик начала обработки видеофайла
   * @param event Данные события
   */
  private onStartProcessing = (event: VideoModel | ImportVideoModel) => {
    this.StartProcessingEvent.trigger(event);
  };

  /**Подписаться на событие ухода со страницы */
  private subscribeOnExit = () => {
    const self = this;
    // Обработчик события beforeunload
    window.addEventListener("beforeunload", function (event) {
      if (
        self.IsEnableExitAlert &&
        self.ActiveUploads.value.some(
          (file) =>
            (file.isFileModel() || file.isVideoModel()) &&
            [
              FileUploadStatusEnum.Pause,
              FileUploadStatusEnum.Uploading,
            ].includes(file.Status)
        )
      ) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
    });
  };

  /**
   * Реактивное свойство, содержащее список активных загрузок
   * @returns {ComputedRef<Array<AbstractFileModel>>} Вычисляемое свойство с массивом текущих загрузок
   * @example
   * // Отслеживание активных загрузок
   * watch(() => manager.ActiveUploads.value, (uploads) => {
   *   console.log('Активные загрузки:', uploads);
   * });
   */
  readonly ActiveUploads: ComputedRef<Array<AbstractFileModel>> = computed(
    () => [
      ...this.fileUploadService.ActiveUploads.value,
      ...this.videoUploadService.ActiveUploads.value,
      ...this.fileImportService.ActiveUploads.value,
      ...this.videoImportService.ActiveUploads.value,
    ]
  );

  /**
   * Проверяет, является ли файл видео по его расширению
   * @param {string} fileName - Название файла
   * @returns {boolean} True если файл является видео
   *
   * @example
   * // Возвращает true для видеофайлов
   * checkIsVideo('video.mp4') => true
   *
   * // Возвращает false для других файлов
   * checkIsVideo('document.pdf') => false
   */
  private checkIsVideo(fileName: string): boolean {
    const ext = fileName.split(".").pop().toLowerCase();
    return ["avi", "mp4", "mkv", "mov", "flv", "webm"].includes(ext);
  }

  /**
   * Разделяет массив файлов на видео и не-видео файлы
   * @param {UploadFileItem[]} items - Массив файлов для разделения
   * @returns {{
   *   videos: UploadFileItem[],
   *   otherFiles: UploadFileItem[]
   * }} Объект с разделенными файлами
   *
   * @example
   * // Разделяет файлы по типу
   * const { videos, otherFiles } = separateVideoUploadItems([
   *   { File: new File([], 'video.mp4') },
   *   { File: new File([], 'document.pdf') }
   * ]);
   */
  private separateVideoUploadItems(items: Array<UploadFileItem>): {
    videos: UploadFileItem[];
    otherFiles: UploadFileItem[];
  } {
    return items.reduce(
      (result, item) => {
        if (this.checkIsVideo(item.File.name)) {
          result.videos.push(item);
        } else {
          result.otherFiles.push(item);
        }
        return result;
      },
      { videos: [] as UploadFileItem[], otherFiles: [] as UploadFileItem[] }
    );
  }

  /**
   * Разделяет массив элементов импорта на видео и обычные файлы
   * @param {Array<ImportItem>} items - Массив элементов для импорта, которые нужно разделить
   * @returns {{
   *   videos: ImportItem[],
   *   otherFiles: ImportItem[]
   * }} Объект с разделенными элементами, где:
   *   - videos: массив элементов импорта видеофайлов
   *   - otherFiles: массив элементов импорта обычных файлов (не видео)
   *
   * @example
   * // Пример использования:
   * const importItems = [
   *   { FileName: 'movie.mp4', Url: 'http://example.com/movie.mp4' },
   *   { FileName: 'document.pdf', Url: 'http://example.com/doc.pdf' }
   * ];
   *
   * const { videos, otherFiles } = separateVideoImportItems(importItems);
   * // videos: [{ FileName: 'movie.mp4', Url: 'http://example.com/movie.mp4' }]
   * // otherFiles: [{ FileName: 'document.pdf', Url: 'http://example.com/doc.pdf' }]
   */
  private separateVideoImportItems = (
    items: Array<ImportItem>
  ): {
    videos: ImportItem[];
    otherFiles: ImportItem[];
  } => {
    return items.reduce(
      (result, item) => {
        if (this.checkIsVideo(item.FileName)) {
          result.videos.push(item);
        } else {
          result.otherFiles.push(item);
        }
        return result;
      },
      { videos: [] as ImportItem[], otherFiles: [] as ImportItem[] }
    );
  };

  /**
   * Загружает один файл с автоматическим определением типа (файл/видео)
   * @param {UploadParams} params - Параметры загрузки
   * @returns {Promise<FileModel | VideoModel>} Модель загруженного файла
   * @example
   * const result = await manager.upload({
   *   File: new File(['content'], 'doc.pdf'),
   *   Location: { FolderId: '...' },
   *   Options: { ... }
   * });
   */
  upload(params: UploadParams): Promise<ResponseModel<FileModel | VideoModel>> {
    if (this.checkIsVideo(params.Item.File.name))
      return this.videoUploadService.upload(params);
    else return this.fileUploadService.upload(params);
  }
  /**
   * Загружает несколько файлов параллельно
   * @param {UploadBatchParams} params - Параметры загрузки
   * @returns {Promise<ResponseModel<Array<FileModel | VideoModel>>>} Массив результатов загрузки
   * @example
   * const results = await manager.uploadBatch([
   *   { File: file1, Location: { FolderId: '...' } },
   *   { File: video1, Location: { FolderId: '...' }, Options: { ... } }
   * ]);
   */
  uploadBatch(
    params: UploadBatchParams
  ): Promise<ResponseModel<Array<FileModel | VideoModel>>> {
    return new Promise<ResponseModel<Array<FileModel | VideoModel>>>(
      async (resolve) => {
        // Разделяем файлы на видео и обычные
        const { videos, otherFiles } = this.separateVideoUploadItems(
          params.Items
        );

        // Создаем модифицированные параметры для каждого типа
        const filesParams: UploadBatchParams = {
          ...params,
          Items: otherFiles,
        };

        const videosParams: UploadBatchParams = {
          ...params,
          Items: videos,
        };

        const [filesResponse, videosResponse] = await Promise.all([
          otherFiles.length
            ? this.fileUploadService.uploadBatch(filesParams)
            : Promise.resolve(
                new ResponseModel({
                  Value: [],
                })
              ),
          videos.length
            ? this.videoUploadService.uploadBatch(videosParams)
            : Promise.resolve(
                new ResponseModel({
                  Value: [],
                })
              ),
        ]);

        // Объединяем результаты
        const allResults = [
          ...(filesResponse.Value || []),
          ...(videosResponse.Value || []),
        ];

        const response =
          [filesResponse, videosResponse].find((resp) => !resp.IsSuccess) ??
          new ResponseModel();

        return resolve(
          new ResponseModel({
            ...response,
            Value: allResults,
          })
        );
      }
    );
  }
  /**
   * Импортирует один файл по URL
   * @param {ImportParams} params - Параметры импорта
   * @returns {Promise<ResponseModel<ImportFileModel | ImportVideoModel>>} Модель импортированного файла
   * @example
   * const result = await manager.import({
   *   Url: 'https://example.com/file.pdf',
   *   FileName: 'document.pdf',
   *   Location: { FolderId: '...' }
   * });
   */
  import(
    params: ImportParams
  ): Promise<ResponseModel<ImportFileModel | ImportVideoModel>> {
    if (this.checkIsVideo(params.Item.FileName))
      return this.videoImportService.import(params);
    else return this.fileImportService.import(params);
  }
  /**
   * Импортирует несколько файлов по URL параллельно
   * @param {ImportBatchParams} params - Параметры импорта
   * @returns {Promise<ResponseModel<Array<ImportFileModel | ImportVideoModel>>>} Массив результатов импорта
   * @example
   * const results = await manager.importBatch([
   *   { Url: 'https://example.com/file1.pdf', FileName: 'doc1.pdf', Location: { FolderId: '...' } },
   *   { Url: 'https://example.com/video.mp4', FileName: 'video.mp4', Location: { FolderId: '...' }, Options: { ... } }
   * ]);
   */
  importBatch(
    params: ImportBatchParams
  ): Promise<ResponseModel<Array<ImportFileModel | ImportVideoModel>>> {
    return new Promise<
      ResponseModel<Array<ImportFileModel | ImportVideoModel>>
    >(async (resolve) => {
      // Разделяем файлы на видео и обычные
      const { videos, otherFiles } = this.separateVideoImportItems(
        params.Items
      );

      // Создаем модифицированные параметры для каждого типа
      const filesParams: ImportBatchParams = {
        ...params,
        Items: otherFiles,
      };

      const videosParams: ImportBatchParams = {
        ...params,
        Items: videos,
      };

      const [filesResponse, videosResponse] = await Promise.all([
        otherFiles.length
          ? this.fileImportService.importBatch(filesParams)
          : Promise.resolve(
              new ResponseModel({
                Value: [],
              })
            ),
        videos.length
          ? this.videoImportService.importBatch(videosParams)
          : Promise.resolve(
              new ResponseModel({
                Value: [],
              })
            ),
      ]);

      // Объединяем результаты
      const allResults = [
        ...(filesResponse.Value || []),
        ...(videosResponse.Value || []),
      ];

      const response =
        [filesResponse, videosResponse].find((resp) => !resp.IsSuccess) ??
        new ResponseModel();

      return resolve(
        new ResponseModel({
          ...response,
          Value: allResults,
        })
      );
    });
  }

  /**
   * Приостанавливает загрузку файла
   * @param {ControlParams} params - Параметры управления
   * @returns {Promise<void>}
   * @example
   * await manager.pause({ FileId: 123 });
   */
  pause(params: ControlParams): boolean {
    if (this.videoUploadService.isFileInActiveUploads(params.FileId))
      return this.videoUploadService.pause(params);
    else if (this.fileUploadService.isFileInActiveUploads(params.FileId))
      return this.fileUploadService.pause(params);

    return false;
  }
  /**
   * Возобновляет приостановленную загрузку
   * @param {ControlParams} params - Параметры управления
   * @returns {boolean}
   * @example
   * await manager.resume({ FileId: 123 });
   */
  resume(params: ControlParams): boolean {
    if (this.videoUploadService.isFileInActiveUploads(params.FileId))
      return this.videoUploadService.resume(params);
    else if (this.fileUploadService.isFileInActiveUploads(params.FileId))
      return this.fileUploadService.resume(params);

    return false;
  }
  /**
   * Отменяет загрузку файла
   * @param {ControlParams} params - Параметры управления
   * @returns {Promise<ResponseModel>}
   * @example
   * await manager.cancel({ FileId: 123 });
   */
  async cancel(params: ControlParams): Promise<ResponseModel> {
    if (this.videoUploadService.isFileInActiveUploads(params.FileId))
      return this.videoUploadService.cancel(params);
    else if (this.fileUploadService.isFileInActiveUploads(params.FileId))
      return this.fileUploadService.cancel(params);
    else if (this.fileImportService.isFileInActiveUploads(params.FileId))
      return this.fileImportService.cancel(params);
    else if (this.videoImportService.isFileInActiveUploads(params.FileId))
      return this.videoImportService.cancel(params);

    return new ResponseModel({
      IsSuccess: false,
    });
  }
}

const fileManagerSymbolKey = Symbol.for("fileManager");

const _window = window as any;
let FileManager = _window[fileManagerSymbolKey] as FileManagerService;
if (!FileManager) {
  FileManager = new FileManagerService();
  _window[fileManagerSymbolKey] = FileManager;
}

export { FileManager };
